<template>
  <v-container>
    <v-row>
      <v-col v-if="clientInformation" cols="12" align="center" justify="center">
        <v-card elevation="2" :loading="loading">
          <v-sheet v-if="!loading" color="grey lighten-4" align="center" class="py-3">
            <v-avatar size="80" color="error" class="align-self-center">
              <img v-if="client.profilePicture" :src="client.profilePicture" />
              <span v-else class="white--text headline">{{ client.shorthandName }}</span>
            </v-avatar>
            <h3 class="py-2">{{ clientInformation.user.firstName }} {{ clientInformation.user.lastName }}</h3>
            <div class="d-inline-flex">
              <h3 class="py-2 mr-4">{{ clientInformation.user.email }}</h3>
              <v-btn icon @click="copyEmail()" class="my-auto">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" slot="activator">
                      <v-icon :color="copyEmailText == 'Copied' ? 'green' : null">mdi-content-copy</v-icon>
                    </div>
                  </template>
                  <span>{{ copyEmailText }}</span>
                </v-tooltip>
              </v-btn>
              <v-btn v-if="loginAsToken" icon @click="copyLoginAs()" class="my-auto">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on" slot="activator">
                      <v-icon :color="copyLoginAsText == 'Copied' ? 'green' : null">mdi-lock-clock</v-icon>
                    </div>
                  </template>
                  <span>{{ copyLoginAsText }}</span>
                </v-tooltip>
              </v-btn>
            </div>
          </v-sheet>
          <v-card-text>
            <div class="larger-font fake-link" @click="showPoints = true">{{ points }} Points</div>
          </v-card-text>
          <v-card-title>Personal Information</v-card-title>
          <ValidationObserver ref="obs">
            <v-simple-table v-if="!loading">
              <tbody>
                <tr>
                  <td><strong>Full name</strong></td>
                  <td v-if="!edit">{{ clientInformation.user.firstName }} {{ clientInformation.user.lastName }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="First name" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="firstName"
                        v-model="userData.firstName"
                        placeholder=""
                      ></v-text-field>
                    </ValidationProvider>
                    <ValidationProvider rules="required" name="Last name" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="lastName"
                        v-model="userData.lastName"
                        placeholder=""
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td><strong>Age</strong></td>
                  <td>{{ age }}</td>
                </tr>
                <tr>
                  <td><strong>Gender</strong></td>
                  <td v-if="!edit">{{ clientInformation.gender }}</td>
                  <td v-else>
                    <v-select v-model="userData.gender" :items="genderOptions"> </v-select>
                  </td>
                </tr>
                <tr>
                  <td><strong>Date of Birth</strong></td>
                  <td v-if="!edit">
                    {{ clientInformation.dateOfBirth | moment("DD MMMM YYYY") }}
                    <div v-if="birthday(clientInformation.dateOfBirth)">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon color="primary" v-bind="attrs" v-on="on" class="mb-2 ml-2">
                            mdi-cake-variant
                          </v-icon>
                        </template>
                        <span>Wish {{ clientInformation.firstName }} a happy birthday!</span>
                      </v-tooltip>
                      <span>({{ birthday(clientInformation.dateOfBirth).text }})</span>
                    </div>
                  </td>
                  <td v-else>
                    <v-menu ref="menu" v-model="menu" :close-on-content-click="false" transition="scale-transition">
                      <template v-slot:activator="{ on, attrs }">
                        <ValidationProvider mode="lazy" name="DOB" v-slot="{ errors, valid }">
                          <v-text-field
                            v-model="userData.dateOfBirth"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                            :error-messages="errors"
                            :success="valid"
                          ></v-text-field>
                        </ValidationProvider>
                      </template>
                      <v-date-picker
                        ref="picker"
                        v-model="userData.dateOfBirth"
                        :max="new Date().toISOString().substr(0, 10)"
                        min="1920-01-01"
                        @change="save_date"
                      ></v-date-picker>
                    </v-menu>
                  </td>
                </tr>
                <tr>
                  <td><strong>Weight</strong></td>
                  <td v-if="!edit">{{ clientInformation.weight }}</td>
                  <td v-else>
                    <ValidationProvider name="Weight" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="weight"
                        v-model="userData.weight"
                        placeholder="Weight"
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td><strong>Height</strong></td>
                  <td v-if="!edit">{{ clientInformation.height }}</td>
                  <td v-else>
                    <ValidationProvider name="Height" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="height"
                        v-model="userData.height"
                        placeholder="Height"
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr v-if="clientInformation.dateJoined">
                  <td><strong>Date Joined</strong></td>
                  <td>{{ formatDate(clientInformation.dateJoined) }} {{ joinedAgo }}</td>
                </tr>
                <tr>
                  <td><strong>Email</strong></td>
                  <td v-if="!edit">{{ clientInformation.user.email }}</td>
                  <td v-else>
                    <ValidationProvider rules="required|email" name="email" v-slot="{ errors, valid }">
                      <v-text-field
                        :error-messages="errors"
                        :success="valid"
                        ref="email"
                        v-model="userData.email"
                        placeholder="Email"
                      ></v-text-field>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td><strong>Last login</strong></td>
                  <td>{{ clientInformation.user.lastLogin | moment("YYYY-MM-DD HH:MM") }}</td>
                </tr>
                <tr>
                  <td><strong>Last Active</strong></td>
                  <td>{{ clientInformation.user.userProfile.lastActive | moment("YYYY-MM-DD HH:MM") }}</td>
                </tr>
                <tr>
                  <td><strong>Phone</strong></td>
                  <td v-if="!edit">{{ clientInformation.phone }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="Phone" v-slot="{ errors }">
                      <vue-tel-input
                        mode="international"
                        :default-country="userData.country"
                        v-model="userData.phone"
                        v-on:country-changed="countryChanged"
                      />
                      <span v-if="errors.length > 0" class="error--text">The Phone field is required</span>
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td>Fitness Level</td>
                  <td>{{ clientInformation.fitnessLevel }}</td>
                </tr>
                <tr>
                  <td>Occupation</td>
                  <td>{{ clientInformation.occupation }}</td>
                </tr>
                <tr>
                  <td>Weight goal</td>
                  <td>{{ clientInformation.desiredWeight }}</td>
                </tr>
                <tr>
                  <td>Weight loss goal</td>
                  <td>{{ clientInformation.desiredWeightLoss }}</td>
                </tr>
                <tr>
                  <td>Has Medical Conditions</td>
                  <td>{{ clientInformation.hasMedicalConditions }}</td>
                </tr>
                <tr>
                  <td>Calories</td>
                  <td>{{ clientInformation.calories }}</td>
                </tr>
                <tr>
                  <td><strong>Calculated Serving Size</strong></td>
                  <td v-if="!edit">{{ clientInformation.calculatedServingSize }}</td>
                  <td v-else>
                    <ValidationProvider rules="required" name="caculatedServingSize" v-slot="{ errors, valid }">
                      <v-select
                        :error-messages="errors"
                        :success="valid"
                        ref="caculatedServingSize"
                        v-model="userData.calculatedServingSize"
                        :items="servingSizeOptions"
                        placeholder="Calculated Serving Size"
                      />
                    </ValidationProvider>
                  </td>
                </tr>
                <tr>
                  <td>Workout days:</td>
                  <td>
                    <v-row dense>
                      <v-col cols="auto" v-for="(day, index) in days" :key="index">
                        <v-checkbox
                          v-model="selectedDays"
                          :label="day"
                          :value="day"
                          @click="updateExerciseDays"
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
            <v-alert v-if="showInvalid" type="error" text class="ma-4">Some required fields are missing</v-alert>
            <v-card-actions>
              <v-btn v-if="edit" @click="cancel()" :loading="saving">Cancel</v-btn>
              <v-spacer />
              <v-btn v-if="edit" :loading="saving" @click="save()">Save</v-btn>
              <v-btn v-if="!edit" @click="edit = true">Edit</v-btn>
            </v-card-actions>
          </ValidationObserver>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="showPoints" width="900" class="limited-height" transition="dialog-bottom-transition">
      <client-reward-point-events :userId="userId" />
    </v-dialog>
  </v-container>
</template>

<script>
import {
  CLIENT_INFORMATION_QUERY,
  LOGIN_AS_TOKEN_QUERY,
  UPDATE_PROFILE_MUTATION,
  UPSERT_EXERCISE_DAYS_MUTATION,
} from "@/constants/graphql/client/client";
import { USER_TOTAL_POINTS_QUERY } from "@/graphql/queries/user";
import { YYYYMMDD_readable } from "@/lib/fancy_date";
import BirthdayMixin from "@/components/shared/mixins/BirthdayMixin";
import ClientRewardPointEvents from "@/components/client/blocks/activity/ClientRewardPointEvents";

export default {
  name: "ClientInformation",
  mixins: [BirthdayMixin],
  props: {
    userId: {
      type: [String, Number],
      required: true,
    },
  },
  components: {
    ClientRewardPointEvents,
  },
  data() {
    return {
      loading: true,
      activeProgram: null,
      points: 0,
      saving: false,
      edit: false,
      menu: false,
      showInvalid: false,
      showPoints: false,
      copyEmailText: "Copy email",
      copyLoginAsText: "Copy login-as password",
      days: ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"],
      selectedDays: [],
      client: {
        name: "",
        shorthandName: "",
        profilePicture: null,
      },
      genderOptions: [
        { text: "", value: null },
        { text: "Female", value: "FEMALE" },
        { text: "Male", value: "MALE" },
      ],
      dietaryOptions: [
        { text: "", value: null },
        { text: "No preference", value: "NA" },
        { text: "Vegetarian", value: "VEGETARIAN" },
      ],
      servingSizeOptions: [
        { text: "", value: null },
        { text: "Lite", value: "LITE" },
        { text: "Small", value: "SMALL" },
        { text: "Medium", value: "MEDIUM" },
        { text: "Standard", value: "STANDARD" },
      ],
      data: {
        userId: null,
        dateOfBirth: null,
        firstName: "",
        lastName: "",
        gender: null,
        phone: "",
        whatsappJid: "",
        alternateWhatsappJid: "",
        suburb: "",
        city: "",
        dietaryRequirement: null,
        email: "",
        height: "",
      },
    };
  },
  computed: {
    age() {
      if (this.clientInformation?.dateOfBirth) {
        let dob = this.$moment(this.clientInformation.dateOfBirth).format("YYYY-MM-DD");
        let age = this.$moment().diff(dob, "years");

        return age;
      }

      return "";
    },
    joinedAgo() {
      if (this.clientInformation?.dateJoined) {
        let dateJoined = this.$moment(this.clientInformation.dateJoined);
        return `(${dateJoined.fromNow()})`;
      }

      return "";
    },
  },
  methods: {
    updateExerciseDays() {
      this.$apollo
        .mutate({
          mutation: UPSERT_EXERCISE_DAYS_MUTATION,
          variables: {
            days: this.selectedDays,
            userId: this.userId,
          },
        })
        .then((response) => {
          this.saving = false;
          if (response.data.upsertExerciseDays.success) {
            this.edit = false;
            this.$toasted.success("Information updated", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
          } else {
            this.$toasted.error("Unable to update your Information. Sorry.", {
              icon: "alert",
              duration: 2000,
              position: "bottom-center",
            });
          }
        });
    },
    async copyEmail() {
      await navigator.clipboard.writeText(this.clientInformation.user.username);
      this.copyEmailText = "Copied";
      this.copyLoginAsText = "Copy Login-as password";
    },
    async copyLoginAs() {
      await navigator.clipboard.writeText(this.loginAsToken);
      this.copyLoginAsText = "Copied";
      this.copyEmailText = "Copy email";
    },
    showModal() {
      this.showPoints = true;
    },
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    countryChanged(country) {
      this.userData.country = country.iso2;
    },
    save_date(date) {
      this.$refs.menu.save(date);
    },
    cancel() {
      this.edit = false;
      let client = this.clientInformation;
      this.userData = {
        userId: this.userId,
        dateOfBirth: client.dateOfBirth,
        firstName: client.firstName,
        lastName: client.lastName,
        gender: client.gender,
        phone: client.phone,
        whatsappJid: client.whatsappJid,
        alternateWhatsappJid: client.alternateWhatsappJid,
        suburb: client.suburb,
        city: client.city,
        dietaryRequirement: client.dietaryRequirement,
        email: client.email,
        height: client.height,
        weight: client.weight,
        hubspotContactId: client.hubspotContactId,
        calculatedServingSize: client.calculatedServingSize,
      };
    },
    save() {
      this.$refs.obs.validate().then((valid) => {
        if (!valid) {
          this.showInvalid = true;
        } else {
          this.showInvalid = false;
          this.saving = true;
          delete this.userData["exerciseDays"];
          this.$apollo
            .mutate({
              mutation: UPDATE_PROFILE_MUTATION,
              variables: {
                userProfileInput: this.userData,
              },
              refetchQueries: ["clientInformationQuery"],
            })
            .then((response) => {
              this.saving = false;
              if (response.data.updateProfile.ok) {
                this.edit = false;
                this.$toasted.success("Information updated", {
                  icon: "arm-flex",
                  duration: 2000,
                  position: "bottom-center",
                });
              } else {
                this.$toasted.error("Unable to update your Information. Sorry.", {
                  icon: "alert",
                  duration: 2000,
                  position: "bottom-center",
                });
              }
            });
        }
      });
    },
  },
  apollo: {
    clientInformation: {
      query: CLIENT_INFORMATION_QUERY,
      variables() {
        return {
          userId: this.userId,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
          let client = response.data.clientInformation;
          this.client.shorthandName = `${client.user.firstName[0]} ${client.user.lastName[0]}`;
          this.client.profilePicture = client.profilePicture;

          this.userData = {
            userId: this.userId,
            dateOfBirth: client.dateOfBirth,
            firstName: client.user.firstName,
            lastName: client.user.lastName,
            gender: client.gender,
            phone: client.phone,
            whatsappJid: client.whatsappJid,
            alternateWhatsappJid: client.alternateWhatsappJid,
            suburb: client.suburb,
            city: client.city,
            dietaryRequirement: client.dietaryRequirement,
            email: client.user.email,
            height: client.height,
            weight: client.weight,
            hubspotContactId: client.hubspotContactVid,
            calculatedServingSize: client.calculatedServingSize,
            country: client.country.code ? client.country.code : "ZA",
            exerciseDays: client.exerciseDays,
          };
          //          {{ clientInformation.exerciseDays }}
          //[ { "id": "453", "day": "TUESDAY", "__typename": "ExerciseDayType" }, { "id": "454", "day": "SATURDAY", "__typename": "ExerciseDayType" } ]
          this.selectedDays = client.exerciseDays.map((day) => day.day);
        }
      },
    },
    loginAsToken: {
      query: LOGIN_AS_TOKEN_QUERY,
    },
    rewardPoints: {
      query: USER_TOTAL_POINTS_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;
          this.points = response.data?.rewardPoints?.totalPoints ?? 0;
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.larger-font {
  font-size: 45px;
}
</style>
